import type { PDFDocument, PDFFont } from 'pdf-lib';
import type { CompositionTypography } from '../../model';
import { StaticExportError } from '../model';
import type { AnnotationFontFamilyId, AnnotationFontWeight } from '../../../annotations/typography';

export type PdfTypography = Omit<
  Pick<CompositionTypography, 'fontFamily' | 'fontStyle' | 'fontWeight'>,
  'fontFamily' | 'fontWeight'
> & {
  readonly fontFamily: AnnotationFontFamilyId;
  readonly fontWeight: CompositionTypography['fontWeight'] | AnnotationFontWeight;
};
const assets = import.meta.glob('./fontAssets/*.ttf', {
  query: '?url',
  import: 'default',
  eager: true,
}) as Record<string, string>;
export const pdfFontKey = (type: PdfTypography) =>
  `${type.fontFamily}-${typeof type.fontWeight === 'number' ? ({ 400: 'regular', 500: 'medium', 600: 'semibold', 700: 'bold' } as const)[type.fontWeight] : type.fontWeight}-${type.fontStyle}`;
export const pdfFontAsset = (key: string): string => {
  const url = assets[`./fontAssets/${key}.ttf`];
  if (!url)
    throw new StaticExportError(
      'fonts',
      'Could not load the authored PDF font. Raster PDF remains available.',
    );
  return url;
};
export const loadPdfFontBytes = async (key: string): Promise<Uint8Array> => {
  const response = await fetch(pdfFontAsset(key), { signal: AbortSignal.timeout(10_000) });
  if (!response.ok) throw new Error('Font asset unavailable.');
  return new Uint8Array(await response.arrayBuffer());
};

export interface PdfTextRun {
  readonly text: string;
  readonly font: PDFFont;
  /** Font design metrics normalized to one em, independent of PDF raster detail. */
  readonly ascent: number;
  readonly descent: number;
}

/** One export owns its font resources. Only used variants/subsets are fetched and embedded. */
export async function createPdfFonts(document: PDFDocument, loadBytes = loadPdfFontBytes) {
  const { default: fontkit } = await import('@pdf-lib/fontkit');
  document.registerFontkit(fontkit);
  const fonts = new Map<string, Promise<Omit<PdfTextRun, 'text'>>>();
  const get = (key: string) => {
    let font = fonts.get(key);
    if (!font) {
      font = loadBytes(key).then(async (bytes) => {
        const metrics = fontkit.create(bytes);
        return {
          font: await document.embedFont(bytes, { subset: true, customName: `PlaceioMaps-${key}` }),
          ascent: metrics.ascent / metrics.unitsPerEm,
          descent: Math.abs(metrics.descent) / metrics.unitsPerEm,
        };
      });
      fonts.set(key, font);
    }
    return font;
  };
  return async (text: string, typography: PdfTypography): Promise<PdfTextRun[]> => {
    try {
      if (!text) return [];
      const latin = await get(pdfFontKey(typography));
      const supported = new Set(latin.font.getCharacterSet());
      const runs: (Omit<PdfTextRun, 'text'> & { text: string })[] = [];
      for (const character of text.replace(/[\r\n\t]/g, ' ')) {
        const font = latin.font;
        if (!supported.has(character.codePointAt(0)!)) {
          throw new Error('Unsupported font character.');
        }
        const previous = runs.at(-1);
        if (previous?.font === font) previous.text += character;
        else runs.push({ text: character, ...latin });
      }
      return runs;
    } catch (cause) {
      throw new StaticExportError(
        'fonts',
        'Could not embed the authored PDF text. Raster PDF remains available.',
        { cause },
      );
    }
  };
}
export type PdfFonts = Awaited<ReturnType<typeof createPdfFonts>>;
